import React from 'react';
import { Box, Button, Container, Typography, useTheme, alpha } from '@mui/material';
import { NorthEast } from '@mui/icons-material';
import { COMPANY, ERP_FEATURES } from '../../data/siteContent';
import { MotionBox, fadeInUp, useMotionProps } from '../motion';
import SectionHeading from './SectionHeading';

const ErpHighlight = () => {
  const theme = useTheme();
  const motionProps = useMotionProps();

  return (
    <Box
      component="section"
      aria-labelledby="erp-heading"
      sx={{
        py: { xs: 9, md: 13 },
        position: 'relative',
        overflow: 'hidden',
        color: '#fff',
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 45%, ${theme.palette.secondary.main} 100%)`
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(${alpha('#fff', 0.06)} 1px, transparent 1px),
                            linear-gradient(90deg, ${alpha('#fff', 0.06)} 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 0%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 0%, transparent 80%)'
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(40rem 24rem at 15% 0%, ${alpha('#fff', 0.16)} 0%, transparent 60%),
                       radial-gradient(36rem 22rem at 85% 100%, ${alpha('#fff', 0.1)} 0%, transparent 60%)`
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <SectionHeading
          id="erp-heading"
          eyebrow="Flagship Platform"
          title="Introducing Our Comprehensive ERP System"
          subtitle="A unified platform connecting inventory, HR, finance, and CRM, with a real-time centralized dashboard."
          onDark
        />

        <MotionBox {...motionProps(fadeInUp(0.1))} sx={{ maxWidth: 980, mx: 'auto' }}>
          <Typography variant="h5" component="h3" sx={{ fontWeight: 800, mb: 1.5, textAlign: 'center' }}>
            Everything Connected, Everything Controlled
          </Typography>
          <Typography sx={{ mb: 5, lineHeight: 1.8, color: alpha('#fff', 0.9), textAlign: 'center' }}>
            From inventory to HR, planning to customer relationships, access all your operations in one place.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: { xs: 2, md: 2.5 },
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
            }}
          >
            {ERP_FEATURES.map(({ icon: Icon, title, description }, i) => (
              <MotionBox
                key={title}
                {...motionProps(fadeInUp(0.05 * i))}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '56px 1fr',
                  columnGap: 2,
                  alignItems: 'center',
                  p: 2.5,
                  borderRadius: 3,
                  ...theme.surfaces.glassDark,
                  transition: 'background-color 0.25s ease, transform 0.25s ease',
                  '&:hover': { transform: 'translateY(-3px)' }
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha('#fff', 0.18),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff'
                  }}
                >
                  <Icon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 800, color: '#fff' }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.85) }}>
                    {description}
                  </Typography>
                </Box>
              </MotionBox>
            ))}
          </Box>
        </MotionBox>

        <MotionBox {...motionProps(fadeInUp(0.2))} sx={{ textAlign: 'center', mt: { xs: 6, md: 9 } }}>
          <Typography variant="h4" component="h3" sx={{ fontWeight: 800, mb: 2, color: '#fff' }}>
            Experience Our ERP System Today
          </Typography>
          <Typography variant="h6" component="p" sx={{ color: alpha('#fff', 0.9), mb: 4, maxWidth: 620, mx: 'auto', fontWeight: 400 }}>
            Transform your business operations with our comprehensive enterprise resource planning solution.
          </Typography>
          {/* White surface with primary-coloured label: legible on the gradient band. */}
          <Button
            variant="contained"
            size="large"
            component="a"
            href={COMPANY.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<NorthEast sx={{ fontSize: 18 }} />}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1.05rem',
              borderRadius: 3,
              background: '#fff',
              color: 'primary.dark',
              boxShadow: `0 12px 32px ${alpha('#000', 0.2)}`,
              '&:hover': {
                background: alpha('#fff', 0.92),
                boxShadow: `0 16px 40px ${alpha('#000', 0.28)}`,
                transform: 'translateY(-2px)'
              },
              '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 3 }
            }}
          >
            Launch ERP System
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default ErpHighlight;
